import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    type OnInit,
    output,
    signal,
    type WritableSignal,
} from '@angular/core';
import { type FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, filter, map, startWith } from 'rxjs';

import type { InstagramPageInfoDto } from '@malou-io/package-dto';

import { isNotNil, PlatformKey } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { DiagnosticHttpService } from ':core/http-services/diagnostic.http-service';
import { LogoPathResolverPipe } from ':shared/pipes';

@Component({
    selector: 'app-input-instagram-search',
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        TranslateModule,
        LogoPathResolverPipe,
    ],
    templateUrl: './input-instagram-search.component.html',
    styleUrl: './input-instagram-search.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputInstagramSearchComponent implements OnInit {
    private readonly _diagnosticHttpService = inject(DiagnosticHttpService);

    readonly diagnostic = input.required<Diagnostic | null>();
    readonly instagramControl = input.required<FormControl<string | null>>();
    readonly instagramPage = input.required<InstagramPageInfoDto | null>();
    readonly instagramPageValidated = input.required<boolean>();
    readonly reset = input<boolean>();

    readonly instagramPageSelection = output<void>();
    readonly instagramPageChange = output<InstagramPageInfoDto | null>();
    readonly onShouldUpdateInstagramPage = output<string>();

    PlatformKey = PlatformKey;

    readonly instagramProfilePictureUrl = computed(() => this.instagramPage()?.business_discovery?.profile_picture_url ?? '');
    readonly instagramPageName = computed(
        () => this.instagramPage()?.business_discovery?.name ?? this.instagramPage()?.business_discovery?.username ?? ''
    );
    readonly instagramPageDescription = computed(() => this.instagramPage()?.business_discovery?.biography ?? '');

    readonly isPrefilledInstagramPageValid: WritableSignal<boolean> = signal(false);

    readonly shouldDisableControl = computed(() => {
        const diagnostic = this.diagnostic();
        if (this.isPrefilledInstagramPageValid() && diagnostic?.isMalouRestaurant) {
            this.instagramControl().disable();
        }
    });

    readonly prefilledInstagramPageName = computed(() => {
        const diagnostic = this.diagnostic();
        if (diagnostic && diagnostic.instagramPage?.userName && diagnostic.isMalouRestaurant) {
            this._diagnosticHttpService.getInstagramPage$(diagnostic?.instagramPage?.userName).subscribe((instagramPage) => {
                if (instagramPage) {
                    this.instagramPageChange.emit(instagramPage);
                    this._diagnosticHttpService
                        .updateWithInstagramPageInfo$(diagnostic.id, instagramPage.business_discovery.username)
                        .subscribe(() => {
                            this.instagramPageSelection.emit();
                            this.instagramControl().setValue(instagramPage.business_discovery.name);
                            this.isPrefilledInstagramPageValid.set(true);
                            this.onShouldUpdateInstagramPage.emit(instagramPage.business_discovery.username);
                        });
                }
            });
        }
    });

    constructor() {
        effect(() => {
            this.prefilledInstagramPageName();
            this.shouldDisableControl();
            if (this.reset()) {
                this.instagramControl()?.setValue('');
            }
        });
    }

    ngOnInit(): void {
        this._initControl();
    }

    private _initControl(): void {
        this.instagramControl()
            .valueChanges.pipe(
                startWith(''),
                debounceTime(300),
                filter((value) => isNotNil(value) && !this.instagramControl().disabled),
                filter((value) => {
                    const instagramPage = this.instagramPage();
                    if (instagramPage) {
                        return value !== instagramPage.business_discovery.name;
                    }
                    return true;
                }),
                map((value) => {
                    if (typeof value !== 'string') {
                        return '';
                    }
                    return value[0] === '@' ? value.slice(1) : value;
                })
            )
            .subscribe((value) => {
                if (value === '') {
                    this.instagramPageChange.emit(null);
                    return;
                }
                this._diagnosticHttpService.getInstagramPage$(value).subscribe((instagramPage) => {
                    this.instagramPageChange.emit(instagramPage);
                });
            });
    }

    validateInstagramPage(): void {
        const diagnostic = this.diagnostic();
        const instagramPageInfo = this.instagramPage();
        this.instagramPageSelection.emit();
        if (diagnostic && instagramPageInfo && this.instagramControl().value) {
            this._diagnosticHttpService
                .updateWithInstagramPageInfo$(diagnostic.id, instagramPageInfo.business_discovery.username)
                .subscribe({
                    next: () => {
                        this.onShouldUpdateInstagramPage.emit(instagramPageInfo.business_discovery.username);
                    },
                });
        }
    }
}
